import TableCardv2 from "../../../components/TableCardv2";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import {
  buscarVerbasEsocialPorTexto,
  buscaVerbasComValoresPorTexto,
} from "../../../services/api-esocial.service";
import SnackBar from "../../../components/SnackBar";
import { useEffect, useState, useRef, memo, useMemo, useCallback } from "react";
import { debounce } from "lodash";

const TablesAnalizar = memo(({ idEmpresa, data, typeData, loading, ...rest }) => {
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [filteredData, setFilteredData] = useState([]);
  const debounceFunctionRef = useRef(null);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const substituirZerosPorPontos = useCallback((str) => {
    return str.replace(/([A-Z]+)(0+)(\d+)/, "$1...$3");
  }, []);

  const handleFormatDataEsocial = useCallback((obj) => {
    return obj.map((item) => ({
      ...item,
      codigoRubricaCompleto: item.codigoRubrica,
      codigoRubrica: substituirZerosPorPontos(item.codigoRubrica),
      inicioValidade: item.inicioValidade.split("-").reverse().join("/"),
      fimValidade: item.fimValidade.split("-").reverse().join("/"),
    }));
  }, [substituirZerosPorPontos]);

  const handleFormatDataVerbasComValores = useCallback((obj) => {
    return obj.map((item) => ({
      ...item,
      codRubricaCompleto: item.codRubrica,
      codRubrica: substituirZerosPorPontos(item.codRubrica),
      periodoApurado: item.periodoApurado.split("-").reverse().join("/"),
      valorRubrica: new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(parseFloat(item.valorRubrica.trim())),
      composicaoProjeto: item.composicaoProjeto ? "Sim" : "Não",
    }));
  }, [substituirZerosPorPontos]);

  const { columns, formattedData, header } = useMemo(() => {
    if (!typeData) return { columns: [], formattedData: [], header: {} };

    switch (typeData) {
      case "valoresEsocial":
        debounceFunctionRef.current = (value) => handleDebounceVerbasEsocial(value);
        return {
          columns: [
            { key: "codigoRubrica", label: "Código da Rubrica" },
            { key: "descricaoRubrica", label: "Descrição da Rubrica" },
            { key: "inicioValidade", label: "Início da Validade" },
            { key: "fimValidade", label: "Fim (Calculado) da Validade" },
            { key: "tipoRubrica", label: "Tipo da Rubrica" },
            { key: "codigoIncidencia", label: "Código da Incidência" },
            { key: "descricaoIncidencia", label: "Descrição da Incidência" },
            { key: "categoriaIncidencia", label: "Categoria da Incidência" },
          ],
          formattedData: handleFormatDataEsocial(data),
          header: {
            icon: <AssessmentRoundedIcon />,
            title: "Verbas eSocial",
          },
        };

      case "verbasComValores":
        debounceFunctionRef.current = (value) => handleDebounceVerbasComValores(value);
        return {
          columns: [
            { key: "periodoApurado", label: "Período de Apuração" },
            { key: "idTabelaRubrica", label: "Id da Tabela de Rubricas" },
            { key: "codRubrica", label: "Código da Rubrica" },
            { key: "descricaoRubrica", label: "Descrição" },
            { key: "tipoRubrica", label: "Tipo de Rubrica" },
            { key: "valorRubrica", label: "Valor da Rubrica" },
            { key: "incidencia", label: "Incidencia" },
            { key: "composicaoProjeto", label: "Composição de projeto" },
          ],
          formattedData: handleFormatDataVerbasComValores(data),
          header: {
            icon: <AttachMoneyRoundedIcon />,
            title: "Verbas com Valores",
          },
        };

      default:
        return { columns: [], formattedData: [], header: {} };
    }
  }, [typeData, data, handleFormatDataEsocial, handleFormatDataVerbasComValores]);

  useEffect(() => {
    setFilteredData(formattedData); // sempre que mudar os dados formatados
  }, [formattedData]);

  const handleDebounceVerbasEsocial = useCallback(async (value) => {
    try {
      if (!value) {
        setFilteredData(handleFormatDataEsocial(data));
        return;
      }

      const response = await buscarVerbasEsocialPorTexto(idEmpresa, value);
      const filtradas = handleFormatDataEsocial(response.verbas || []);
      setFilteredData(filtradas);
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: error.response?.mensagem || "Erro ao buscar dados",
      });
      setFilteredData(handleFormatDataEsocial(data));
    }
  }, [idEmpresa, data, handleFormatDataEsocial]);

  const handleDebounceVerbasComValores = useCallback(async (value) => {
    try {
      if (!value) {
        setFilteredData(handleFormatDataVerbasComValores(data));
        return;
      }

      const response = await buscaVerbasComValoresPorTexto(idEmpresa, value);
      const filtradas = handleFormatDataVerbasComValores(response.folha || []);
      setFilteredData(filtradas);
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: error.response?.mensagem || "Erro ao buscar dados",
      });
      setFilteredData(handleFormatDataVerbasComValores(data));
    }
  }, [idEmpresa, data, handleFormatDataVerbasComValores]);

  const functionDebounce = useMemo(() => debounce((value) => {
    if (debounceFunctionRef.current) {
      debounceFunctionRef.current(value);
    }
  }, 400), []);

  return (
    <>
      <TableCardv2
        columns={columns}
        data={filteredData}
        debounce={functionDebounce}
        loading={loading}
        header={header}
        {...rest}
      />
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
});

export default TablesAnalizar;
